import { useTranslation } from "react-i18next";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import { PaymentLinkStatus } from "enums/PaymentLink";

type StatusKey = PaymentLinkStatus | "EXPIRED" | "default";

const statusMapping: Record<
  StatusKey,
  { glyph: string; name: string; tone: ChipTone }
> = {
  [PaymentLinkStatus.ACTIVE]: {
    glyph: "active",
    name: "Active",
    tone: "positive",
  },
  [PaymentLinkStatus.INACTIVE]: {
    glyph: "inactive",
    name: "Inactive",
    tone: "neutral",
  },
  EXPIRED: { glyph: "expired", name: "Expired", tone: "warning" },
  default: { glyph: "pending", name: "Unknown Status", tone: "neutral" },
};

interface RenderPaymentLinkStatusProps {
  status: PaymentLinkStatus;
  expiresAt?: number | null;
  textOnly?: boolean;
}

const RenderPaymentLinkStatus: React.FC<RenderPaymentLinkStatusProps> = ({
  status,
  expiresAt,
  textOnly = false,
}) => {
  const { t } = useTranslation();

  const { glyph, name, tone } =
    statusMapping[
      resolveStatusKey({
        status,
        expiresAt,
      })
    ] || statusMapping.default;

  if (textOnly) {
    return <>{t(name)}</>;
  }

  return (
    <Chip
      tone={tone}
      size="sm"
      label={t(name)}
      leading={<StatusGlyph status={glyph} tone={tone} />}
    />
  );
};

function resolveStatusKey({
  status,
  expiresAt,
}: Omit<RenderPaymentLinkStatusProps, "textOnly">): StatusKey {
  if (status !== PaymentLinkStatus.ACTIVE) {
    return status;
  }
  if (expiresAt && expiresAt <= Date.now()) {
    return "EXPIRED";
  }

  return PaymentLinkStatus.ACTIVE;
}

export default RenderPaymentLinkStatus;
